/**
 * Proximity navigation — menu items swell as the cursor nears them.
 *
 * Each item is measured against the pointer; the falloff is eased so
 * neighbours are partly affected and the block reads as magnetic rather
 * than as one item switching on.
 *
 * Immersive + fine pointer only. Standard mode leaves the list alone
 * (CSS pins the size with !important).
 */

/* Inlined rather than imported — see the note at the top of mode.js. */
const getMode = () =>
  document.documentElement.classList.contains('mode-immersive') ? 'immersive' : 'standard';
const canBeImmersive = () =>
  window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const nav = document.querySelector('[data-proximity-nav]');
if (nav) {
  const MIN = 14;
  const MAX = 22;
  const RADIUS = 140;

  const items = Array.from(nav.querySelectorAll('.nav__item'));
  let boxes = [];
  let pointerX = -9999;
  let pointerY = -9999;
  let frame = null;
  let running = false;

  const measure = () => {
    boxes = items.map((item) => {
      const rect = item.getBoundingClientRect();
      return { item, cx: rect.right, cy: rect.top + rect.height / 2 };
    });
  };

  /**
   * Cubic falloff on normalised proximity.
   *
   * The obvious choice — easeOutCubic, 1-(1-t)³ — is wrong here: it saturates,
   * so an item 40px away still reads at ~96% and the whole block swells as one.
   * Cubing proximity directly concentrates the effect under the pointer and
   * lets neighbours trail off, which is what makes it feel magnetic:
   *
   *   0px → 22.0    40px → 16.4    100px → 14.2
   *  20px → 19.1    70px → 14.9    140px → 14.0
   */
  const falloff = (t) => t * t * t;

  const render = () => {
    for (const { item, cx, cy } of boxes) {
      const dx = pointerX - cx;
      const dy = pointerY - cy;
      const distance = Math.hypot(dx, dy);
      const influence = distance > RADIUS ? 0 : falloff(1 - distance / RADIUS);
      item.style.fontSize = `${(MIN + (MAX - MIN) * influence).toFixed(2)}px`;
    }
    frame = requestAnimationFrame(render);
  };

  const onMove = (event) => {
    pointerX = event.clientX;
    pointerY = event.clientY;
  };

  const onLeave = () => {
    pointerX = -9999;
    pointerY = -9999;
  };

  const onResize = () => measure();

  function start() {
    if (running) return;
    running = true;
    measure();
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', onResize, { passive: true });
    window.addEventListener('resize', onResize);
    document.addEventListener('pointerleave', onLeave);
    frame = requestAnimationFrame(render);
  }

  function stop() {
    if (!running) return;
    running = false;
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('scroll', onResize);
    window.removeEventListener('resize', onResize);
    document.removeEventListener('pointerleave', onLeave);
    if (frame) cancelAnimationFrame(frame);
    items.forEach((item) => { item.style.fontSize = ''; });
  }

  const sync = () => {
    if (getMode() === 'immersive' && canBeImmersive()) start();
    else stop();
  };

  sync();
  document.addEventListener('ss:modechange', sync);
}
